import { Button, Icon, Link } from "@material-ui/core";
import CalendarRange from "mdi-material-ui/CalendarRange";
import Numeric from "mdi-material-ui/Numeric";
import SortAlphabeticalVariant from "mdi-material-ui/SortAlphabeticalVariant";
import Star from "mdi-material-ui/Star";
import Update from "mdi-material-ui/Update";
import React, { Component } from "reactn";
import { withTranslation } from "react-i18next";
import styled from "styled-components";

import { ActionPermissionsObject, WorkspaceResourceType, PermissionActionType, WorkspacePlanTier } from "common/interfaces";
import Banner from "js/Components/Banner";
import { Breadcrumb } from "js/Components/Breadcrumb";
import WorkspaceController from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import { ds } from "js/core/models/dataService";
import { Slide } from "js/core/models/slide";
import { trackActivity } from "js/core/utilities/utilities";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { app } from "js/namespaces";
import { ShowConfirmationDialog, ShowDialogAsync, ShowErrorDialog, ShowInputDialog, ShowWarningDialog } from "js/react/components/Dialogs/BaseDialog";
import { Gap, Gap20 } from "js/react/components/Gap";
import { FlexBox } from "js/react/components/LayoutGrid";
import { NoMatchNotice, NoTeamSlideNotice, SharedSlideGeneralNotice, SharedSlideNotice } from "js/react/components/Notice";
import Spinner from "js/react/components/Spinner";
import { RoundIconButton, UIPane } from "js/react/components/UiComponents";
import { themeColors } from "js/react/sharedStyles";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import { ShowDialog } from "js/react/components/Dialogs/BaseDialog";
import TeamSlideDialog from "js/react/views/TeamResources/dialogs/TeamSlideDialog";
import { TeamResourceTypes } from "js/react/views/TeamResources/TeamResources";
import { _ } from "js/vendor";

import { teamSlidesDataService } from "../DataServices";
import { FilterDropDown } from "./Components/FilterDropDown";
import {
    InnerWhiteFrameContainer,
    SearchBarContainer,
    SearchBarInnerContainer,
    SlideSearchInput,
    UIPaneResultsContainer
} from "./Components/SearchBox";
import { SortMenu } from "./Components/SortMenu";
import { TeamSlideThumbnail } from "./Components/TeamSlideThumbnail";
import { MultiSelectThumbnailGrid } from "./Components/ThumbnailGrid";

const logger = getLogger(LogGroup.TEAMS);

const getFilterOptions = t => [
    {
        id: "all_content",
        name: t("All Content")
    },
    {
        id: "resource:metadata:name",
        name: t("Title")
    },
    {
        id: "resource:metadata:description",
        name: t("Description")
    },
    {
        id: "slide:text",
        name: t("Slide Content")
    },
    {
        id: "resource:tagsText",
        name: t("Tags")
    },
    {
        id: "slide:templateTitle",
        name: t("Template")
    },
];

const AddSlideContainer = styled.div`
    display: flex;
    align-items: flex-end;
    justify-content: center;
    width: 100%;
    background: ${themeColors.lightGray};
    padding-top: 20px;
    //this is to cover any thumbnail spinners behind the container
    z-index: 1000;
`;

const AddSlideButton = styled(Button)`
    background: #2EB4E5 !important;
    color: white !important;
    min-width: 150px !important;
    margin-bottom: 15px !important;
    padding: 10px 20px !important;

    &:disabled {
        background: white !important;
        color: #A9A9A9 !important;
    }
`;

const StatsContainer = styled.div`
    background: #f1f1f1;   
    width: 100%;
    padding: 15px;
    font-weight: 600;
    .MuiIcon-root {
        margin-right: 10px;
    }
    & > div + div {
        margin-top: 10px;
    }
`;

export const SharedSlidesPane = WorkspaceController.withInitializedState(withTranslation()(
    class SharedSlidesPane extends Component {
        constructor(props) {
            super(props);
            const { t } = props;

            this.state = {
                isLoaded: false,
                query: this.global.searchSlidesQuery ?? "",
                sort: { field: "modifiedAt", reverse: true },
                tags: [{ id: "all_tags", name: t("All Tags") }],
                selectedContent: "all_content",
                selectedTag: "all_tags",
                searchResults: [],
                selectedItems: []
            };

            this.scrollRef = React.createRef();
        }

        get sortOptions() {
            const { t } = this.props;

            return [{
                value: "name", label: t("Name"), icon: <SortAlphabeticalVariant />
            }, {
                value: "ratedByCount", label: t("Rating"), reverse: true, icon: <Star />
            }, {
                value: "presentationCount", label: t("Popularity"), reverse: true, icon: <Numeric />
            }, {
                value: "createdAt", label: t("Created At"), reverse: true, icon: <CalendarRange />
            }, {
                value: "modifiedAt", label: t("Modified At"), reverse: true, icon: <Update />
            }];
        }

        componentDidMount() {
            teamSlidesDataService.ensureIndexPrepared(AppController.workspaceId);

            this.fetchSearchResults();
        }

        componentDidUpdate(prevProps, prevState) {
            const { isTemplate, resources } = this.props;
            const { query, selectedContent, selectedTag } = this.state;

            if (
                prevState.query !== query ||
                prevState.selectedContent !== selectedContent ||
                prevState.selectedTag !== selectedTag ||
                prevProps.isTemplate !== isTemplate
            ) {
                this.setState({ isLoaded: false, selectedItems: [] });
                this.fetchSearchResults();
            } else if (!_.isEqual(prevProps.resources, resources)) {
                // Reload index
                teamSlidesDataService.dispose()
                    .then(() => {
                        this.fetchSearchResults();
                    });
            }
        }

        setStateAsync(stateUpdate) {
            return new Promise(resolve => this.setState(stateUpdate, resolve));
        }

        fetchSearchResults = async () => {
            const { isManageLibrary, isTemplate, t } = this.props;
            const { selectedContent, query, selectedTag } = this.state;

            const allTagsMap = {};

            // Filter resources by type and build tag counts
            WorkspaceController.resources
                .filter(resource => {
                    const isCorrectType = isTemplate
                        ? resource.resourceType === WorkspaceResourceType.SLIDE_TEMPLATE
                        : resource.resourceType === WorkspaceResourceType.SLIDE;

                    if (!isCorrectType) return false;

                    if (!isManageLibrary) {
                        return !resource.metadata?.isDisabled;
                    }

                    return true;
                })
                .forEach(resource => {
                    resource.tags.forEach(tag => {
                        if (!allTagsMap[tag]) {
                            allTagsMap[tag] = 1;
                        } else {
                            allTagsMap[tag]++;
                        }
                    });
                });

            // Fetch filtered search results
            let searchResults = await teamSlidesDataService.search({
                fullTextSearchQuery: query,
                tag: selectedTag !== "all_tags" ? selectedTag : undefined,
                fullTextSearchByFields: (selectedContent && selectedContent !== "all_content") ? [selectedContent] : [],
                resourceTypes: isTemplate ? [WorkspaceResourceType.SLIDE_TEMPLATE] : [WorkspaceResourceType.SLIDE]
            });

            if (!isManageLibrary) {
                searchResults = searchResults.filter(({ isDisabled }) => !isDisabled);
            }

            // Build tags from all available tags with their total counts
            const tags = [
                { id: "all_tags", name: t("All Tags") },
                ...Object.entries(allTagsMap).map(([tag, totalCount]) => {
                    return {
                        id: tag,
                        name: `${tag} (${totalCount})`
                    };
                })
            ];

            // Add selected tag if it's not in the list
            if (selectedTag !== "all_tags" && !tags.some(({ id }) => id === selectedTag)) {
                tags.push({ id: selectedTag, name: `${selectedTag} (0)` });
            }

            await this.setStateAsync({
                searchResults,
                tags,
                isLoaded: true
            });
        }

        handleSelectTag = event => {
            this.setState({ selectedTag: event.target.value });
        }

        handleSearch = query => {
            this.setState({ query });
        }

        handleSort = sort => {
            this.setState({ sort });
        }

        handleFilterChange = event => {
            this.setState({ selectedContent: event.target.value });
        }

        handleAddItem = async () => {
            const { resourceType } = this.props;
            AppController.showSlideEditor({ resourceType });
        }

        handleEditSlide = async item => {
            const { resourceType } = this.props;
            AppController.showSlideEditor({ slideId: item.id, resourceType });
        }

        copySlide = async (itemModel, updateProps = {}) => {
            const { t } = this.props;

            try {
                await withProgressDialog({
                    title: t("Copying slide..."),
                    action: async () => {
                        const {
                            name,
                            id,
                            tags,
                            description,
                            isDisabled,
                            isTemplate,
                        } = { ...itemModel, ...updateProps };

                        const slide = new Slide({ id });
                        await slide.load();

                        const slideModel = _.cloneDeep(slide.attributes);
                        delete slideModel.id;
                        delete slideModel.libraryItemId;
                        delete slideModel.thumbnails;
                        delete slideModel.presentationId;

                        const newSlide = new Slide(slideModel, {
                            userId: app.userId,
                            autoSync: false
                        });
                        await newSlide.load();

                        const resource = {
                            resourceId: newSlide.id,
                            resourceType: isTemplate ? WorkspaceResourceType.SLIDE_TEMPLATE : WorkspaceResourceType.SLIDE,
                            // Hard to track what we can be served with... so let's make sure we're always passing an array
                            tags: tags
                                ? Array.isArray(tags)
                                    ? tags
                                    : Object.keys(tags)
                                : [],
                            folderIds: [],
                            metadata: {
                                name,
                                description,
                                isDisabled,
                                contentModifiedAt: newSlide.get("modifiedAt"),
                                usedByUsers: [],
                                usedInPresentations: [],
                                usedInPublicPresentations: [],
                                ratedByUsers: [],
                                createdBy: app.user.id,
                                modifiedBy: app.user.id,
                            }
                        };

                        await WorkspaceController.createWorkspaceResource({ resource, defaultPermissions: "shared" });
                    }
                });
            } catch (err) {
                logger.error(err, "[SharedSlidesPane] copySlide failed");

                ShowErrorDialog({
                    error: t("An error occurred while copying the slide."),
                    message: err.message
                });
            }
        }

        handleCopySlide = async item => {
            const { t } = this.props;

            if (item.isTemplate) {
                await this.copySlide(item, {
                    isTemplate: false,
                    name: t("Copy of {{name}}", { name: item.name })
                });

                ShowWarningDialog({
                    title: t("A copy of this slide is now available to your team as a finished, uneditable Shared Slide."),
                    icon: "/images/ui/icons/done-check.gif"
                });

                return;
            }

            await this.copySlide(item, {
                isTemplate: true,
                name: t("Copy of {{name}}", { name: item.name }),
                isDisabled: false
            });

            ShowWarningDialog({
                title: t("A copy of this slide is now available to your team as an editable Slide Template."),
                icon: "/images/ui/icons/done-check.gif"
            });
        }

        handleDuplicateSlide = async item => {
            const { t } = this.props;

            const name = await ShowInputDialog({
                title: t("Enter a name for the copied slide"),
                value: t("Copy of {{name}}", { name: item.name })
            });

            if (!name) {
                return;
            }

            await this.copySlide(item, {
                name
            });
        }

        handleDeleteSlide = async item => {
            const { isTemplate, trackActivity, title, t } = this.props;
            const { resource: { resourceId, resourceType } } = item;
            const resourceName = resourceType === WorkspaceResourceType.SLIDE_TEMPLATE ? t("Slide Template") : t("Shared Slide");
            const dialogResourceName = isTemplate ? title : resourceName;

            const deleteResource = async () => {
                try {
                    await withProgressDialog({
                        title: t("Deleting {{resourceName}}...", { resourceName }),
                        action: () => WorkspaceController.deleteWorkspaceResource({ resourceId, resourceType }, { trackActivity })
                    });
                } catch (err) {
                    logger.error(err, "[SharedSlidesPane]  failed", { resourceId, resourceType });

                    ShowErrorDialog({
                        error: t("An error occurred while deleting the Team Slide."),
                        message: (
                            <>
                                <p>
                                    <strong>{t("Error")}:</strong> {err.message}
                                </p>
                                <p>{t("We apologize for the inconvenience. Please contact us at support@beautiful.ai.")}</p>
                            </>
                        )
                    });
                }
            };

            if (!isTemplate) {
                if (await ShowConfirmationDialog({
                    title: t("Are you sure you want to delete this {{resourceName}}?", { resourceName }),
                    message: <>
                        <span>
                            {t("Deleting a Shared Slide permanently removes it from your Team Resources.")}{" "}
                            {t("Existing instances of this slide will no longer update automatically and will be editable by presentation editors.")}{" "}
                            <Link onClick={() => this.showTeamSlideDialog(item)}>{t("See where this slide is being used.")}</Link>
                        </span>
                        <Gap20 />
                        <StatsContainer>
                            <FlexBox left middle>
                                <Icon>star</Icon>
                                {t("{{count}} {{likes}}", {
                                    count: item.ratedByCount,
                                    likes: item.ratedByCount === 1 ? t("like") : t("likes")
                                })}
                            </FlexBox>
                            <FlexBox left middle>
                                <Icon>person</Icon>
                                {t("Used by {{count}} {{people}}", {
                                    count: item.usedByCount,
                                    people: item.usedByCount === 1 ? t("person") : t("people")
                                })}
                            </FlexBox>
                            <FlexBox left middle>
                                <Icon>cloud_download</Icon>
                                {t("Used in {{count}} {{presentations}}", {
                                    count: item.presentationCount,
                                    presentations: item.presentationCount === 1 ? t("presentation") : t("presentations")
                                })}
                            </FlexBox>
                        </StatsContainer>
                    </>,
                    okButtonLabel: t("Delete Shared Slide"),
                    buttonOptions: {
                        acceptButtonColor: "red"
                    }
                })) {
                    await deleteResource();
                }
                return;
            }

            if (await ShowConfirmationDialog({
                title: t("Are you sure you want to delete this {{resourceName}}?", { resourceName: dialogResourceName }),
                message: t("Deleting a {{resourceName}} permanently removes it from your {{resourceName}} library and prevents it from being used in any future presentations.", { resourceName: dialogResourceName }),
                buttonOptions: { cancelButtonVariant: "outlined", acceptButtonColor: "red" },
                okButtonLabel: t("PERMANENTLY DELETE TEAM SLIDE")
            })) {
                await deleteResource();
            }
        }

        handleAddSelectedSlides = async () => {
            const { onSelected } = this.props;
            const { selectedItems } = this.state;

            const props = {
                ...ds.selection.presentation.getAnalytics(),
                slide_ids: selectedItems.map(item => item.id),
            };
            trackActivity("Presentation", "LibrarySlideAdded", null, null, props);

            onSelected(selectedItems);
        }

        showTeamSlideDialog = async item => {
            await ShowDialogAsync(TeamSlideDialog, {
                slideId: item.id
            });
        }

        render() {
            const {
                isManageLibrary,
                title,
                resourceType,
                t
            } = this.props;
            const {
                query,
                selectedTag,
                selectedContent,
                selectedItems,
                tags,
                sort
            } = this.state;

            const canUpgradeToManageSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.MANAGE);
            const canManageSharedResources = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].manage;
            const canUseSharedResources = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].use;

            let panelStyles, searchStyles, scrollStyles;
            if (!isManageLibrary) {
                panelStyles = {
                    paddingTop: 4,
                    paddingRight: 20,
                    paddingBottom: 0,
                    paddingLeft: 20
                };
                searchStyles = {
                    position: "initial"
                };
            } else {
                scrollStyles = {
                    height: "calc(100% - 49px)"
                };
            }

            return (
                <UIPane>
                    <SearchBarContainer className="search-container">
                        {isManageLibrary && canManageSharedResources &&
                            <>
                                <Breadcrumb>{title}s</Breadcrumb>
                                <RoundIconButton onClick={this.handleAddItem}><Icon>add</Icon></RoundIconButton>
                                <Gap size={60} />
                            </>
                        }
                        {canUseSharedResources &&
                            <SearchBarInnerContainer>
                                <SlideSearchInput
                                    query={query}
                                    onSearch={this.handleSearch}
                                    placeholder={t("Search team slides...")}
                                />
                                <FilterDropDown
                                    onChange={this.handleFilterChange}
                                    selectedFilter={selectedContent}
                                    filterOptions={getFilterOptions(t)}
                                />
                                <FilterDropDown
                                    onChange={this.handleSelectTag}
                                    selectedFilter={selectedTag}
                                    filterOptions={tags}
                                />
                                <SortMenu
                                    disabled={query.length > 0}
                                    sortOptions={this.sortOptions}
                                    selectedOption={sort?.field}
                                    onSetSort={this.handleSort}
                                />
                            </SearchBarInnerContainer>
                        }
                        {canUpgradeToManageSharedResources &&
                            <FlexBox center fillWidth padding="40px 0px 0px 0px">
                                <Banner
                                    headerText={t("Unlock Team {{resourceName}}", {
                                        resourceName: resourceType === TeamResourceTypes.SLIDE_TEMPLATES ? t("Slide Templates") : t("Shared Slides")
                                    })}
                                    bodyText={resourceType === TeamResourceTypes.SLIDE_TEMPLATES
                                        ? t("Upgrade to a Team plan for instant access to searchable, reusable slide library.")
                                        : t("Upgrade to a Team plan and sync slide edits across every deck.")}
                                    buttonLabel={t("Upgrade")}
                                    onButtonClick={() => ShowDialog(ChangePlanDialog,
                                        {
                                            featuredPlan: WorkspacePlanTier.TEAM,
                                            analytics: { cta: resourceType === TeamResourceTypes.SLIDE_TEMPLATES ? "slide_templates" : "shared_slides" }
                                        })}
                                />
                            </FlexBox>
                        }
                    </SearchBarContainer>
                    <UIPaneResultsContainer style={(canUpgradeToManageSharedResources) ? { paddingTop: "20px" } : {}}>
                        {this.renderSlides(canUpgradeToManageSharedResources)}
                    </UIPaneResultsContainer>
                    {!isManageLibrary && canManageSharedResources &&
                        <AddSlideContainer>
                            <AddSlideButton disabled={!selectedItems.length} onClick={this.handleAddSelectedSlides}>
                                {t("Add Selected Slides")}
                            </AddSlideButton>
                        </AddSlideContainer>
                    }
                </UIPane>
            );
        }

        renderSlides(canUpgradeToManageSharedResources) {
            const {
                isManageLibrary,
                resourceType
            } = this.props;
            const {
                isLoaded,
                query,
                searchResults,
                sort
            } = this.state;

            if (!isLoaded) {
                return <Spinner />;
            }
            if (!searchResults.length) {
                if (!query) {
                    if (resourceType === TeamResourceTypes.SLIDE_TEMPLATES) {
                        if (isManageLibrary) return <SharedSlideNotice />;
                        else {
                            // We added this because we we are not in the manageing library
                            // We should present to any user this default message regardless of
                            // their role
                            return (<SharedSlideGeneralNotice />);
                        }
                    }
                    return <NoTeamSlideNotice />;
                } else {
                    return <NoMatchNotice />;
                }
            }

            const sortedResults = sort
                ? _.sortBy(searchResults, item => {
                    if (typeof item[sort.field] === "string") {
                        return item[sort.field].toLowerCase().trim();
                    } else {
                        return item[sort.field];
                    }
                })
                : searchResults;
            if (sort?.reverse) {
                sortedResults.reverse();
            }

            return (<InnerWhiteFrameContainer>
                <MultiSelectThumbnailGrid
                    columns={3}
                    items={sortedResults}
                    allowMultiSelect={!isManageLibrary}
                    thumbnailClass={TeamSlideThumbnail}
                    thumbnailProps={{
                        isManageLibrary,
                        canUpgradeToManageSharedResources,
                        onEditSlide: this.handleEditSlide,
                        onDeleteSlide: this.handleDeleteSlide,
                        onCopySlide: this.handleCopySlide,
                        onDuplicateSlide: this.handleDuplicateSlide
                    }}
                    onItemSelected={selectedItems => this.setState({ selectedItems })}
                    onDoubleClick={canUpgradeToManageSharedResources ? undefined : (slide => {
                        if (isManageLibrary) {
                            this.showTeamSlideDialog(slide);
                        } else {
                            this.setState({ selectedItems: [slide] }, () => {
                                this.handleAddSelectedSlides();
                            });
                        }
                    })}
                />
            </InnerWhiteFrameContainer>);
        }
    }
));
